import React, { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Select from "react-select";

import useUrlTab from "@/src/hooks/useUrlTab";

import AnalyticsInstitution from "./AnalyticsInstitution";
import OfferAPI from "../../api/OfferAPI";
import "../../css/admin/users.scss";
import "../../css/admin/utils/item-status.scss";
import ItemStatus from "../../utils/ItemStatus";
import CappedPage from "../common/CappedPage";
import NavTabs from "../common/NavTabs";
import { getSelectClassNames } from "../form/Select";
import Breadcrumbs from "../utils/Breadcrumbs";
import ContainerHeader from "../utils/ContainerHeader";
import SimpleImageUpload from "../utils/SimpleImageUpload";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type { OfferPlanListItem } from "../../api/OfferAPI.types";
import type { NavTabItem } from "../common/NavTabs";

const TAB_INFORMATION = "information";
const TAB_STATISTICS = "statistics";
const AVAILABLE_TAB_LIST = [TAB_INFORMATION, TAB_STATISTICS];
const OFFER_SELECT_CLASS_NAMES = getSelectClassNames();

interface EduUserEdu {
  type?: string;
  nbr_users?: number;
  max_session?: number;
  authentication_name?: string;
}

export interface EduFormItem {
  uuid?: string;
  edu_name?: string;
  label?: string;
  avatar?: string | ArrayBuffer | null;
  avatar_url?: string;
  status?: string;
  enabled?: boolean;
  offer_uuid?: string | null;
  offer_plan_uuid?: string | null;
  storefront_project_uuid?: string;
  user_edu: EduUserEdu;
  inserted_at?: string;
  updated_at?: string;
  is_deletable?: boolean;
  is_editable?: boolean;
  isFetched?: boolean;
}

interface EDUAdminFormProps {
  item: EduFormItem;
  onSubmit: (user: EduFormItem) => void;
  type: string;
}

interface SelectOption {
  value?: string;
  label: string;
}

interface OfferPlanOption {
  value?: string;
  label: string;
  offer_uuid?: string;
}

function toEduFormItem(item: EduFormItem): EduFormItem {
  return { ...item, label: item.edu_name };
}

export function EDUAdminForm({
  onSubmit,
  type,
  item,
}: EDUAdminFormProps): JSX.Element {
  const { t } = useTranslation("editor-edu");
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const getOfferPlansQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getOfferPlans,
    params: [{ projectSlug }],
  });

  const { data: offerPlans, isLoading } = useQuery(getOfferPlansQuery);

  const { activeTab, setActiveTab } = useUrlTab({
    availableTabs: AVAILABLE_TAB_LIST,
    defaultTab: TAB_INFORMATION,
  });

  const [user, setUser] = useState<EduFormItem>(toEduFormItem(item));

  useEffect(() => {
    if (item.isFetched) {
      setUser(toEduFormItem(item));
    }
  }, [item]);

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    event.preventDefault();

    const reader = new FileReader();
    const file = event.target.files?.[0];

    if (!file) return;

    reader.onloadend = () => {
      setUser({
        ...user,
        avatar: reader.result,
      });
    };

    reader.readAsDataURL(file);
  };

  const onChangeStudents = (
    domEvent: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setUser({
      ...user,
      user_edu: {
        ...user.user_edu,
        nbr_users: parseInt(domEvent.target.value, 10) || 0,
      },
    });
  };

  const onChangeMaxConnexion = (
    domEvent: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setUser({
      ...user,
      user_edu: {
        ...user.user_edu,
        max_session: parseInt(domEvent.target.value, 10) || 0,
      },
    });
  };

  const onChangeTitle = (target: { value: string }): void => {
    setUser({
      ...user,
      label: target.value,
    });
  };

  const onChangeAuthName = (
    domEvent: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setUser({
      ...user,
      user_edu: {
        ...user.user_edu,
        authentication_name: domEvent.target.value,
      },
    });
  };

  const getOfferPlanLabel = (offerPlan?: OfferPlanListItem): string => {
    if (!offerPlan) return "";

    const {
      frequency,
      resolution,
      currency,
      type: planType,
      offer,
    } = offerPlan;

    const offerPlanFrequency =
      frequency % 12 === 0
        ? t("editor-offers:year", { count: frequency / 12 })
        : t("editor-offers:month", { count: frequency });
    const rentalDuration = `${offerPlan.rental_duration} ${t("editor-offers:days")}`;

    const durationString =
      planType === "svod" ? offerPlanFrequency : rentalDuration;

    return `${offer.label}: ${
      planType === "svod" ? "SVOD" : "Pack"
    } - ${durationString} - ${resolution} - ${currency}`;
  };

  const onOffer = (selection: SingleValue<OfferPlanOption>): void => {
    if (!selection) return;

    setUser({
      ...user,
      offer_uuid: selection.offer_uuid,
      offer_plan_uuid: selection.value,
    });
  };

  const onChangeState = (enabled: boolean): void => {
    setUser({
      ...user,
      status: enabled ? "enabled" : "disabled",
    });
  };

  const save = (): void => {
    onSubmit(user);
  };

  const breadcrumbs = [
    {
      name: t("eduTitle"),
      url: "/institutions",
    },
    {
      name: t(type),
    },
  ];

  const rawCurrentOfferPlan = offerPlans?.data?.find(
    (offerPlan) => offerPlan.uuid === user.offer_plan_uuid,
  );

  const currentOfferPlan: OfferPlanOption = {
    value: rawCurrentOfferPlan?.uuid,
    label: getOfferPlanLabel(rawCurrentOfferPlan),
    offer_uuid: rawCurrentOfferPlan?.offer.offer_uuid,
  };

  const offerPlanOptions: OfferPlanOption[] =
    offerPlans?.data.map((offerPlan) => ({
      value: offerPlan.uuid,
      label: getOfferPlanLabel(offerPlan),
      offer_uuid: offerPlan.offer.offer_uuid,
    })) ?? [];

  const eduTypeOptions: SelectOption[] = [
    { value: "cas", label: t("cas") },
    { value: "liblynx", label: t("liblynx") },
    { value: "openid", label: t("openid") },
  ];

  const currentEduType: SelectOption = {
    value: user.user_edu.type,
    label: t(user.user_edu.type ?? ""),
  };

  const handleEduTypeChange = (selection: SingleValue<SelectOption>): void => {
    if (!selection) return;

    setUser({
      ...user,
      user_edu: {
        ...user.user_edu,
        type: selection.value,
      },
    });
  };

  const isNew = type === "new";

  const institutionCreationDate = user.inserted_at
    ? moment(user.inserted_at)
    : null;

  const hasTabs = !isNew && !!user.uuid;
  const isFormTab = !hasTabs || activeTab === TAB_INFORMATION;

  const informationForm = (
    <div className="editor-tile-container m-0!">
      <div className="editor-form-section section-separator">
        <div className="editor-form-section-title">{t("logo")}</div>
        <SimpleImageUpload
          format="1_1"
          id="edu"
          onImageChange={handleImageChange}
          value={user.avatar ?? user.avatar_url}
        />
      </div>
      <div className="editor-form-section">
        <div className="editor-form-section-title">{t("students")}</div>
        <div className="editor-form-input-box-h max-w-[350px]">
          <input
            className="editor-form-input-title"
            min="0"
            onChange={onChangeStudents}
            type="number"
            value={user.user_edu.nbr_users ?? 0}
          />
        </div>
      </div>
      <div className="editor-form-section">
        <div className="editor-form-section-title">{t("max_connexion")}</div>
        <div className="editor-form-input-box-h max-w-[350px]">
          <input
            className="editor-form-input-title"
            min="0"
            onChange={onChangeMaxConnexion}
            type="number"
            value={user.user_edu.max_session ?? 0}
          />
        </div>
      </div>
      <div className="editor-form-section section-separator edu-type-name-container">
        <div className="edu-type-select-container">
          <div className="editor-form-section-title">
            {t("authentication_type")}
          </div>
          <div className="editor-form-input-box-h">
            <Select<SelectOption>
              closeMenuOnSelect
              className="editor-select"
              classNamePrefix="slct"
              name="form-field-name"
              onChange={handleEduTypeChange}
              options={eduTypeOptions}
              placeholder={t("select")}
              value={currentEduType}
            />
          </div>
        </div>
        <div className="edu-name-input-container">
          <div className="editor-form-section-title">
            {t("authentication_name")}
          </div>
          <div className="editor-form-input-box-h">
            <div className="editor-form-input">
              <input
                className="editor-form-input-title"
                onChange={onChangeAuthName}
                type="text"
                value={user.user_edu.authentication_name ?? ""}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="editor-form-section">
        <div className="editor-form-section-title">{t("offer")}</div>
        <div className="max-w-125">
          <Select<OfferPlanOption>
            closeMenuOnSelect
            unstyled
            classNames={OFFER_SELECT_CLASS_NAMES}
            isLoading={isLoading}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            name="form-field-name"
            onChange={onOffer}
            options={offerPlanOptions}
            placeholder={t("select")}
            value={currentOfferPlan}
          />
        </div>
      </div>
    </div>
  );

  const tabList: readonly NavTabItem[] = [
    {
      value: TAB_INFORMATION,
      label: t("editor-analytics:tabs.information"),
      content: informationForm,
    },
    {
      value: TAB_STATISTICS,
      label: t("editor-analytics:tabs.statistics"),
      content: user.uuid ? (
        <AnalyticsInstitution
          institutionCreationDate={institutionCreationDate}
          institutionUuid={user.uuid}
        />
      ) : null,
    },
  ];

  return (
    <CappedPage contentClassName="editor-edu-form-container pt-[30px] pb-15">
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        defaultLabel={t("unamed")}
        item={user}
        itemStatus={ItemStatus.getEduStatus(user)}
        onChangeLabel={isFormTab ? onChangeTitle : undefined}
        onChangeState={isFormTab ? onChangeState : undefined}
        onSave={isFormTab ? save : undefined}
        type="edu"
      />
      {hasTabs ? (
        <NavTabs
          onValueChange={setActiveTab}
          tabList={tabList}
          value={activeTab}
        />
      ) : (
        informationForm
      )}
    </CappedPage>
  );
}

export default EDUAdminForm;
